import {Component, computed, DestroyRef, effect, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {map, Observable} from 'rxjs';
import {CampaignStoreService} from '../../../campaña/services/campaign-store.service';
import {ReportControlsComponent, Scenario} from '../../components/report-controls/report-controls.component';
import {AnalysisMode} from '../../components/filters-report/reporte-filtros.component';
import {ReportesTitleComponent} from '../../components/header-report/reportes-title.component';
import {ReportePreviewComponent} from '../../components/preview-report/reporte-preview.component';
import {ReporteDetalleComponent} from '../../components/detail-report/reporte-detalle.component';
import {EmptyStateComponent} from '@/app/shared/components/empty-state/empty-state.component';
import {DemographicKey} from '../../services/overview-demographics.service';
import {AnalysisDemographicFilters} from '../../infrastructure/analysis.api.types';
import {ReportFiltersStateService} from "../../components/filters-report/service/report-filters-state.service";
import {ReportScenarioComponent} from '@/app/features/reportes/components/report-scenario/report-scenario.component';
import {PdfExportService} from '@/app/shared/services/pdf-export.service';
import {ThemeService} from "@/app/core/services/theme/theme.service";
import {IThemeContent} from "@/themes/theme.interface";

@Component({
  selector: 'app-guia3-report',
  standalone: true,
  templateUrl: './guia3-report.html',
  styleUrl: './guia3-report.scss',
  imports: [
    ReportesTitleComponent,
    ReportePreviewComponent,
    ReporteDetalleComponent,
    EmptyStateComponent,
    ReportControlsComponent,
    ReportScenarioComponent,
  ],
})
export class Guia3ReportComponent implements OnInit {
  // Señales para el contenido del tema
  protected themeContent: WritableSignal<IThemeContent | null>;
  protected reportContent;
  protected detailContent;
  // Propiedades del componente
  protected respondedCount$: Observable<number>;
  protected respondedCountSignal;
  protected hasData;
  protected readonly campaignId;
  // Inyecciones de servicios
  private store: CampaignStoreService;

  // Señal para el título de la página
  protected pageData = signal<{ title: string; subTitle: string; }>({title: '', subTitle: ''});
  private destroyRef: DestroyRef;
  private reportFiltersStateService: ReportFiltersStateService;
  private pdfExportService: PdfExportService;
  private themeService: ThemeService;
  private readonly selectedCampaignId$: Observable<string | null>;

  protected analysisMode: AnalysisMode = 'final-assessments';
  protected hasAppliedFilters = false;
  protected scenarios: Scenario[] = [];
  protected isExporting = false;
  protected activeFacets: DemographicKey[] = [];
  protected activeFilters: Partial<AnalysisDemographicFilters> = {};

  constructor() {
    // 1. Inyectar servicios
    this.store = inject(CampaignStoreService);
    this.destroyRef = inject(DestroyRef);
    this.reportFiltersStateService = inject(ReportFiltersStateService);
    this.pdfExportService = inject(PdfExportService);
    this.themeService = inject(ThemeService);

    // 2. Inicializar propiedades que dependen de los servicios
    this.themeContent = this.themeService.themeContent;
    this.reportContent = computed(() => this.themeContent()?.reports);
    this.detailContent = computed(() => this.reportContent()?.reportDetailComponent);

    this.respondedCount$ = this.store.respondedCount$;
    this.respondedCountSignal = toSignal(this.respondedCount$, {initialValue: 0});
    this.hasData = computed(() => this.respondedCountSignal() > 0);
    this.selectedCampaignId$ = this.store.selectedCampaignClient$.pipe(
      map(c => c?.publicId ?? null)
    );
    this.campaignId = toSignal(this.selectedCampaignId$, {initialValue: null});

    // 3. Lógica del constructor
    this.reportFiltersStateService.clearState();
    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scenarios = [];
        this.hasAppliedFilters = false;
        this.analysisMode = 'final-assessments';
      });

    effect(() => {
      const title = this.reportContent()?.guideThreeReportComponent?.title ?? '';
      this.pageData.set({title: title, subTitle: ''});
    });
  }

  ngOnInit(): void {
  }

  removeScenario(id: string) {
    this.scenarios = this.scenarios.filter(s => s.id !== id);
    if (this.scenarios.length === 0) {
      this.hasAppliedFilters = false;
      this.analysisMode = 'final-assessments';
    }
  }

  async exportToPdf() {
    this.isExporting = true;
    // Esperar un poco más para que los gráficos se rendericen completamente
    await new Promise(resolve => setTimeout(resolve, 2000));
    await this.pdfExportService.exportToPdf('.pdf-export', 'reporte-guia3.pdf');
    this.isExporting = false;
  }
}
